import { Dispatch, SetStateAction, useState } from 'react';

import { ContainedButton } from '../../../components/buttons';
import { AppStates, ProductChangeEvent } from '../../../types';
import { useConfiguratorDispatch, useRootSelector } from '../../../store';
import {
  useAddBaseProductToCart,
  useSendMultiConfigSessionToCart
} from '../../cart/useAddToCart';
import {
  selectAllConfigSnapshots,
  selectMultiConfigEnabled,
  switchConfig,
  useValidateMultiConfigNames
} from '../../multiConfig';

interface ShowUpsellsButtonProps {
  isReadyForCart: boolean;
  // See AddToCartButton's belowConfigMinimum for why this is separate from
  // isReadyForCart - it should visually disable the button, not just no-op
  // the click.
  belowConfigMinimum?: boolean;
  cartItemId?: string;
  setAppState: Dispatch<SetStateAction<AppStates>>;
  onClick?: () => void;
  productTag?: string;
  onProductChange?: (event: ProductChangeEvent) => void;
}

export function ShowUpsellsButton({
  isReadyForCart,
  belowConfigMinimum = false,
  setAppState,
  onClick,
  cartItemId,
  productTag,
  onProductChange
}: ShowUpsellsButtonProps) {
  const params = new URLSearchParams(window.location.search);
  const legacyItemKey = params.get('legacy_item_key') || undefined;

  const updatingCartItem = !!(cartItemId || legacyItemKey);

  const [buttonText, setButtonText] = useState(
    updatingCartItem ? 'Save and Continue' : 'Add to Cart'
  );
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const dispatch = useConfiguratorDispatch();
  const multiConfigEnabled = useRootSelector(selectMultiConfigEnabled);
  const configSnapshots = useRootSelector(selectAllConfigSnapshots);
  const isMultiConfigSession = multiConfigEnabled && configSnapshots.length > 0;

  const addBaseProduct = useAddBaseProductToCart(productTag, cartItemId);
  const sendMultiConfigSession = useSendMultiConfigSessionToCart(productTag);
  const validateMultiConfigNames = useValidateMultiConfigNames();

  const handleClick = async () => {
    if (onClick) onClick();
    if (!validateMultiConfigNames()) return;

    if (isReadyForCart) {
      // For editing, just navigate to NEW_ADD_TO_CART screen.
      // AddToCartButton on that screen will handle the full batch update
      // (base product + upsells API + Shopify cart sync).
      if (updatingCartItem) {
        if (onProductChange) {
          onProductChange({
            isUpdate: true
          });
        }
        setAppState(AppStates.NEW_ADD_TO_CART);
        return;
      }

      // For new items, add base product (or, for a multi-config session,
      // every configuration in it) immediately and use the new flow.
      setButtonText('Adding to Cart');
      setButtonDisabled(true);

      const result = isMultiConfigSession
        ? await sendMultiConfigSession()
        : await addBaseProduct();

      if (result.success) {
        // Upsells (and their _parent_item_id linkage) are anchored to
        // configuration #1 - make sure the live editor state (which
        // useAddUpsellToCart reads from) reflects it before moving on.
        if (isMultiConfigSession) {
          dispatch(switchConfig(configSnapshots[0].id));
        }

        if (onProductChange) {
          onProductChange({
            productName: result.productName,
            cartItemId: result.cartItemId,
            isUpdate: false
          });
        }
        setAppState(AppStates.NEW_ADD_TO_CART);
      } else {
        setButtonText(result.error || 'Error adding to cart');
        setButtonDisabled(true);
      }
    }
  };

  return (
    <ContainedButton
      size="large"
      className="mt-5"
      onClick={handleClick}
      disabled={buttonDisabled || belowConfigMinimum}
    >
      {buttonText}
    </ContainedButton>
  );
}
